import { AbsgArrayHelper, LogService } from '@absg/angular-common';
import { EnvironmentService, PrinterService } from '@absg/nim-client-shared';
import { Injectable, OnDestroy } from '@angular/core';
import { Environments } from 'core.enums/environments';
import { PrintingIpcResponse } from 'core.models/printing-ipc-response.model';
import { PrintingLabelIpcRequest } from 'core.models/printing-label-ipc-request.model';
import { PrintingReceiptIpcRequest } from 'core.models/printing-receipt-ipc-request.model';
import { map } from 'rxjs/operators';
import { PrintingEvents } from 'shared.root/constants/printing-events';
import { ElectronApplicationService } from 'shared.root/services/electron-application.service';
import { SubSink } from 'subsink';

interface PrinterCommandRequest {
  printerName: string;
  commandString?: string;
}

@Injectable()
export class IPCRenderPrinterService extends PrinterService implements OnDestroy {
  private ipcRenderer: Electron.IpcRenderer;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  printingSettings: any;
  private subSink = new SubSink();

  constructor(
    private electronApplicationService: ElectronApplicationService,
    private loggingService: LogService,
    private environmentService: EnvironmentService,
  ) {
    super();
    this.subSink.sink = this.environmentService.maxReceiptCount$.pipe(map((value) => {
      this.printingSettings = { maximumTransactionsPerReceipt: value ?? 32 };
    })).subscribe();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  setSettings(): void {
    this.ipcRenderer = this.electronApplicationService.getIpcRenderer();
    const foundMaxTransactionLength = this.environmentService?.environmentConfig?.maxTransactionNumber;
    if (!foundMaxTransactionLength) {
      this.loggingService.logTrace('Printing setting was not valid, defaulting to core 32'); // Remove once we figure out what might cause module order issues.
    }
    this.printingSettings = { maximumTransactionsPerReceipt: foundMaxTransactionLength ?? 32 };
    this.ipcRenderer?.send(PrintingEvents.setPrintSetupRequest, this.environmentService.environmentConfig);
    this.listen();
  }
  printReceipt(printingRequest: PrintingReceiptIpcRequest): void {
    printingRequest.printerName = this.environmentService.getReceiptPrinterName();
    AbsgArrayHelper.each(printingRequest.printingReceiptDocuments, (doc) => {
      doc.pageSettings.shouldSendLengthChange = this.environmentService.getSendLengthToPrinter();
    });
    this.ipcRenderer.send(PrintingEvents.printReceiptsRequest, printingRequest);
  }
  printLabel(printingRequest: PrintingLabelIpcRequest): void {
    printingRequest.printerName = this.environmentService.getLabelPrinterName();
    AbsgArrayHelper.each(printingRequest.printingLabelDocuments, (doc) => {
      // All labels come through here, so instead of finding all the paths we are adding these here for all since they are required document variables labels don't need set
      doc.pageSettings.shouldSendLengthChange = false;
      doc.pageSettings.documentLength = doc.pageSettings?.dimensions?.height;
    });
    this.ipcRenderer.send(PrintingEvents.printLabelsRequest, printingRequest);
  }
  pausePrinter(): void {
    const request = {
      // TODO: Add dynamic parameters here
    } as PrinterCommandRequest;
    request.printerName = this.environmentService.getLabelPrinterName();
    this.ipcRenderer.send(PrintingEvents.pausePrinterRequest, request);
  }
  purgePrinterQueue(): void {
    const request = {

      // TODO: Add dynamic parameters here
    } as PrinterCommandRequest;
    request.printerName = this.environmentService.getLabelPrinterName();
    this.ipcRenderer.send(PrintingEvents.purgeQueueRequest, request);
  }
  resumePrinter(): void {
    const request = {
      // TODO: Add dynamic parameters here
    } as PrinterCommandRequest;
    request.printerName = this.environmentService.getLabelPrinterName();
    this.ipcRenderer.send(PrintingEvents.resumePrinterRequest, request);
  }
  sendCommandString(commandString: string): void {

    // NOTE: All available commands are documented with the absg-nim-client-station-node-native repo
    // under the "/docs/printer-setup-codes.md"
    // https://providersolution.visualstudio.com/PSG/_git/absg-nim-client-station-node-native?path=%2Fdocs%2Fprinter-setup-codes.md&version=GBdevelop&_a=preview
    const request = {
      commandString,
    } as PrinterCommandRequest;
    request.printerName = this.environmentService.getLabelPrinterName();
    this.ipcRenderer.send(PrintingEvents.sendCommandStringRequest, request);
  }
  handlePrintError(printError: string): void {
    const current = this.environmentService.currentEnvironment;
    if (current === Environments.dev
      || current === Environments.test
      || current === Environments.local
    ) {
      /*
        Here we'll print a warning so that you still see it in console,
        However we won't be sending this to appInsights, where we only want real errors and issues
      */
      console.warn(printError);
      return;
    }
    this.handleErrorMessage(printError);
  }
  private listen(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.ipcRenderer?.on(PrintingEvents.printLabelsResponse, (event: any, response: PrintingIpcResponse) => {
      this.onPrintLabelsResponse(event, response);
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.ipcRenderer?.on(PrintingEvents.printReceiptsResponse, (event: any, response: PrintingIpcResponse) => {
      this.onPrintReceiptsResponse(event, response);
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onPrintLabelsResponse(_event: any, response: PrintingIpcResponse): void {
    if (response.isSuccessful) {
      this.handleMessage(`Label Printing Confirmed: ${response}`);
    } else {
      this.handlePrintError(response.errorMessage);
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onPrintReceiptsResponse(_event: any, response: PrintingIpcResponse): void {
    if (response.isSuccessful) {
      this.handleMessage(`Receipt Printing Confirmed: ${response}`);
    } else {
      this.handlePrintError(response.errorMessage);
    }
  }
  private handleMessage(message: string): void {
    if (this.loggingService) {
      this.loggingService.logTrace(message);
    }
  }
  private handleErrorMessage(errorMessage: string): void {
    if (this.loggingService && errorMessage) {
      this.loggingService.logError(new Error(errorMessage));
    }
  }
}
